import React from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faRedoAlt } from "@fortawesome/pro-solid-svg-icons/faRedoAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import DuplicateRunStatus from "@mapmycustomers/shared/enum/DuplicateRunStatus";
import { LoadingSpinner } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { formatRawDate } from "@app/util/formatters";
import useInterval from "@app/util/hook/useInterval";

import { getLastCalculatedDate, getLastDupeRunStatus } from "../../store";
import { checkDupeRunStatus, recalculateDuplicates } from "../../store/actions";

import styles from "./LastCalculated.module.scss";

interface Props {
  lastCalculatedDate?: string;
  lastDupeRunStatus?: DuplicateRunStatus;
  onCheckDupeRunStatus: typeof checkDupeRunStatus.request;
  onRecalculateDuplicates: typeof recalculateDuplicates.request;
}

const LastCalculated: React.FC<Props> = ({
  lastCalculatedDate,
  lastDupeRunStatus,
  onCheckDupeRunStatus,
  onRecalculateDuplicates,
}) => {
  const intl = useIntl();
  useInterval(onCheckDupeRunStatus, true, 5000, true);

  const loading = lastDupeRunStatus === DuplicateRunStatus.ONGOING;
  const complete = lastDupeRunStatus === DuplicateRunStatus.FINISHED;

  return (
    <div className={styles.container}>
      {lastCalculatedDate ? (
        <TextWithInfo
          className={styles.lastCalculatedDate}
          info={intl.formatMessage({
            id: "settings.mergeDuplicates.table.lastCalculatedDate.description",
            defaultMessage:
              "Calculations are run automatically each week. To update your results immediately, please click “Re-calculate.”",
            description: "Merge duplicates - last calculated date - description",
          })}
        >
          {intl.formatMessage(
            {
              id: "settings.mergeDuplicates.table.lastCalculatedDate",
              defaultMessage: "Last calculated: {date}",
              description: "Merge duplicates - last calculated date",
            },
            {
              date: formatRawDate(lastCalculatedDate, "PPPpp"),
            }
          )}
        </TextWithInfo>
      ) : null}
      {loading ? (
        <Row align="middle" className={styles.findingDuplicates} gutter={layout.spacerS}>
          <Col>
            <LoadingSpinner micro />
          </Col>
          <Col>
            {intl.formatMessage({
              id: "settings.mergeDuplicates.table.lastCalculatedDate.findingDuplicates",
              defaultMessage: "Finding duplicates...",
              description: "Merge duplicates - last calculated date - Finding duplicates...",
            })}
          </Col>
        </Row>
      ) : (
        <Row align="middle" gutter={layout.spacerS}>
          {complete && (
            <Col>
              <FontAwesomeIcon className={styles.complete} icon={faCheckCircle} size="xs" />
            </Col>
          )}
          {complete && (
            <Col className={styles.complete}>
              {intl.formatMessage({
                id: "settings.mergeDuplicates.table.lastCalculatedDate.complete",
                defaultMessage: "Complete",
                description: "Merge duplicates - last calculated date - complete",
              })}
            </Col>
          )}
          <Col className={styles.recalculate}>
            <ButtonLink onClick={() => onRecalculateDuplicates()}>
              <Row gutter={layout.spacerS}>
                {!complete && (
                  <Col>
                    <FontAwesomeIcon icon={faRedoAlt} size="xs" />
                  </Col>
                )}
                <Col>
                  {complete
                    ? intl.formatMessage({
                        id: "settings.mergeDuplicates.table.lastCalculatedDate.refresh",
                        defaultMessage: "Refresh",
                        description: "Merge duplicates - last calculated date - refresh action",
                      })
                    : intl.formatMessage({
                        id: "settings.mergeDuplicates.table.lastCalculatedDate.recalculate",
                        defaultMessage: "Re-calculate",
                        description: "Merge duplicates - last calculated date - recalculate action",
                      })}
                </Col>
              </Row>
            </ButtonLink>
          </Col>
        </Row>
      )}
      {loading && (
        <div className={styles.recalculateDescription}>
          {intl.formatMessage({
            id: "settings.mergeDuplicates.table.lastCalculatedDate.recalculateDescription",
            defaultMessage: "This may take up to a few minutes",
            description:
              "Merge duplicates - last calculated date - This may take up to a few minutes",
          })}
        </div>
      )}
      {complete && (
        <div className={styles.refreshThisPageAndGetNewResults}>
          {intl.formatMessage({
            id: "settings.mergeDuplicates.table.lastCalculatedDate.refreshThisPageAndGetNewResults",
            defaultMessage: "Refresh this page and get new results",
            description:
              "Merge duplicates - last calculated date - Refresh this page and get new results",
          })}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  lastCalculatedDate: getLastCalculatedDate(state),
  lastDupeRunStatus: getLastDupeRunStatus(state),
});

const mapDispatchToProps = {
  onCheckDupeRunStatus: checkDupeRunStatus.request,
  onRecalculateDuplicates: recalculateDuplicates.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(LastCalculated);
